/**
 * Utilities for checking authentication token presence in localStorage
 *
 * These are standalone utility functions for checking if auth tokens exist.
 * For Auth0 session checks, use auth0.isAuthenticated()
 *
 * @example
 *  Check if tokens exist before making API call
 * import { hasAuthTokens } from '@/utilities/authTokens';
 * if (!hasAuthTokens()) {
 *   return;
 * }
 *
 * @example
 * Get tokens for manual header injection
 * import { getAuthTokens } from '@/utilities/authTokens';
 * const { accessToken, magicLinkToken } = getAuthTokens();
 */

/**
 * Check if access token exists in localStorage
 * @returns {boolean}
 */
export const hasAccessToken = () => {
  return !!localStorage.getItem('access_token');
};

/**
 * Check if magic link token exists in localStorage
 * @returns {boolean}
 */
export const hasMagicLinkToken = () => {
  return !!localStorage.getItem('magic_link_token');
};

/**
 * Check if both auth tokens exist in localStorage
 * @returns {boolean}
 */
export const hasAuthTokens = () => {
  return hasAccessToken() && hasMagicLinkToken();
};

/**
 * Get both tokens from localStorage
 * @returns {Object} Object containing accessToken and magicLinkToken
 */
export const getAuthTokens = () => {
  return {
    accessToken: localStorage.getItem('access_token'),
    magicLinkToken: localStorage.getItem('magic_link_token'),
  };
};
